/**
 * Shared Color Constants for EAF modules
 * Colors used across multiple EAF equipment modules (Roof, Sidewall, etc.)
 *
 * IMPORTANT: These are shared UI colors for consistent 3D viewer experience.
 * Module-specific domain colors (panel identification) should remain in each module.
 */

// =============================================================================
// 3D VIEWER COLORS - Shared across all EAF 3D visualization components
// =============================================================================

/**
 * Canvas/viewport colors for 3D model rendering area
 */
export const Viewer3DColors = {
  // Canvas background gradient (Light blue)
  CANVAS_GRADIENT_START: "#e8f4fc", // Light blue
  CANVAS_GRADIENT_MIDDLE: "#d4e8f5", // Medium blue
  CANVAS_GRADIENT_END: "#c0dcee", // Darker blue

  // Selection/Hover highlight color (white emissive glow)
  SELECTION_HIGHLIGHT: "#ffffff", // White for selection/hover highlight

  // Loading overlay
  LOADING_OVERLAY: "rgba(0, 0, 0, 0.3)", // Semi-transparent black

  // Tooltip background
  TOOLTIP_BACKGROUND: "rgba(0, 0, 0, 0.85)", // Dark with transparency
} as const;

/**
 * 3D model interaction settings
 * Emissive intensity values for hover/selection effects
 */
export const Viewer3DSettings = {
  // Emissive intensity when hovering over a panel/component
  HOVER_EMISSIVE_INTENSITY: 0.15,

  // Emissive intensity when a panel/component is selected
  SELECTION_EMISSIVE_INTENSITY: 0.15,

  // Default emissive intensity (no hover/selection)
  DEFAULT_EMISSIVE_INTENSITY: 0,
} as const;

/**
 * Material properties for 3D models
 * Used for MeshStandardMaterial configuration
 */
export const Viewer3DMaterials = {
  // Material properties for empty positions (no part installed)
  EMPTY_METALNESS: 0.3,
  EMPTY_ROUGHNESS: 0.7,

  // Material properties for standard parts
  STANDARD_METALNESS: 0.4,
  STANDARD_ROUGHNESS: 0.6,
} as const;

/**
 * Emissive color values (hex) for Three.js materials
 */
export const Viewer3DEmissiveColors = {
  // White emissive for selection/hover highlight
  HIGHLIGHT: 0xffffff,

  // Black emissive for default state (no glow)
  DEFAULT: 0x000000,
} as const;

/**
 * Model scale and geometry constants
 */
export const Viewer3DGeometry = {
  // Scale factor for GLB models (model is ~7m wide, scaled to ~1.4 units)
  MODEL_SCALE: 0.2,

  // Scale factor to convert from mm to meters (for procedural geometry)
  MM_TO_METERS_SCALE: 0.001,

  // Fallback box geometry size (used in Suspense fallback)
  FALLBACK_BOX_SIZE: 0.5,
} as const;

// =============================================================================
// SHARED UI COLORS - Common UI elements across all EAF modules
// =============================================================================

/**
 * Status indicator colors for equipment/part status legend
 * Used in tree panels and status displays
 */
export const StatusIndicatorColors = {
  OK: "#4caf50", // Green - MUI success color
  EMPTY: "#9e9e9e", // Gray - No part installed
  WARNING: "#ff9800", // Orange - MUI warning color
  ALERT: "#f44336", // Red - MUI error color
} as const;

/**
 * Common UI colors for tree panels and data grids
 */
export const TreePanelColors = {
  // Background for offline/inactive rows
  OFFLINE_ROW_BACKGROUND: "rgba(0, 0, 0, 0.04)", // Soft gray
} as const;

/**
 * Shadow map settings for 3D scenes
 */
export const ShadowMapSettings = {
  // Shadow map resolution (width and height in pixels)
  SIZE: 1024,
} as const;
